import { format } from '@rental/i18n'
import { Button, buttonVariants, cn, Notice } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  CalendarClock,
  Car,
  ChartColumn,
  ChartGantt,
  ClipboardList,
  Euro,
  FileWarning,
  Images,
  Lock,
  type LucideIcon,
  Menu as MenuIcon,
  Plus,
  Printer,
  Receipt,
  Scale,
  ScrollText,
  Search,
  Tablet,
  UserCog,
  Users,
  Wallet,
  Wrench,
  X,
} from 'lucide-react'
import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { can } from '../access'
import { useAccess, useSession } from '../access-context'
import { QueueIndicator, StaleBanner } from '../capture/CaptureUi'
import { Dialog } from '../components/Dialog'
import { SaidWords, useSaid } from '../components/Said'
import { StatusChip } from '../components/StatusChip'
import { StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { animate, cssTime, cssToken } from '../motion'
import { type NavPath, navGroups } from '../nav'
import { useOnline } from '../online'
import { useScrollState } from '../scroll'
import { LanguageSwitch, Wordmark } from './Frame'
import { loadSystem, SYSTEM_QUERY_KEY, SYSTEM_REFRESH_MS } from './System'

const ICONS: Readonly<Record<NavPath, LucideIcon>> = {
  '/': CalendarClock,
  '/timeline': ChartGantt,
  '/prezzi': Euro,
  '/bookings': ClipboardList,
  '/kit': Printer,
  '/vehicles': Car,
  '/modelli': Images,
  '/customers': Users,
  '/maintenance': Wrench,
  '/cash': Wallet,
  '/fiscal': Receipt,
  '/fines': FileWarning,
  '/reports': ChartColumn,
  '/reconciliation': Scale,
  '/people': UserCog,
  '/devices': Tablet,
  '/terms': ScrollText,
  '/system': Activity,
}

/**
 * A menu item. The current one, a sub-page of it included, is logo blue and
 * bold in a well, with a bar at its start: never colour alone.
 */
const menuLink =
  'flex min-h-touch items-center gap-3 rounded-sm border-s-4 border-transparent px-3 font-medium text-text no-underline transition-colors hover:bg-surface-well aria-[current=page]:border-brand aria-[current=page]:bg-surface-well aria-[current=page]:font-bold aria-[current=page]:text-brand'

/** Where the sidebar takes over from the top bar and its menu sheet: Tailwind's `lg`. */
const WIDE = '(min-width: 64rem)'

/** Below 640 px a top-bar button keeps its icon, square; its name is still read out. */
const COMPACT_BUTTON = 'max-sm:size-touch max-sm:px-0'
const COMPACT_LABEL = 'max-sm:sr-only'

/**
 * The frame of every unlocked screen (DESIGN.md, "Staff app direction"). From
 * 1024 px a glass sidebar holds the menu and the person; below, a top bar holds
 * the actions and a sheet holds the menu. Whatever must stay in sight stays in
 * the bar that is shown: who is unlocked, unsent photos, the new booking and the lock.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const sheet = useRef<HTMLDialogElement>(null)
  const main = useRef<HTMLElement>(null)
  const bar = useRef<HTMLElement>(null)
  useScreenEntrance(main)
  useTopBarClearance(bar)
  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:bg-surface focus:p-4">
        {m.app.skipToContent}
      </a>
      <div className="depth" aria-hidden="true" />
      <div className="flex flex-1">
        {/* Only the menu scrolls: the new booking above it and the lock below stay in sight. */}
        <aside className="glass-chrome sticky top-0 hidden h-dvh w-3xs shrink-0 flex-col self-start border-e border-rule lg:flex">
          <div className="flex flex-col gap-4 p-4">
            <Wordmark />
            <NewBooking />
            <FindBooking />
          </div>
          <SidebarMenu />
          <div className="flex flex-col gap-3 border-t border-rule p-4">
            {/* S4: what this iPad still holds of an inspection, for those who inspect. */}
            {can(access, 'rentals.manage') ? <QueueIndicator /> : null}
            <Person />
            <LockButton />
          </div>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <header
            ref={bar}
            className="glass-pane-strong sticky top-0 z-10 flex flex-wrap items-center gap-3 px-4 py-2 lg:hidden"
          >
            <Button
              variant="outline"
              className={cn('rounded-sm', COMPACT_BUTTON)}
              aria-haspopup="dialog"
              onClick={() => sheet.current?.showModal()}
            >
              <MenuIcon aria-hidden="true" className="size-4" />
              <span className={COMPACT_LABEL}>{m.nav.menu}</span>
            </Button>
            <div className="flex flex-col items-start">
              <Who />
              <Offline />
            </div>
            <div className="ms-auto flex flex-wrap items-center gap-2">
              {can(access, 'rentals.manage') ? <QueueIndicator /> : null}
              <NewBooking compact />
              <LockButton compact />
            </div>
          </header>
          <main
            ref={main}
            id="main"
            tabIndex={-1}
            className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 lg:p-8"
          >
            <SystemBanner />
            {can(access, 'rentals.manage') ? <StaleBanner /> : null}
            {/* Each screen lays out its own panes: the shell draws none around it. */}
            {children}
          </main>
        </div>
      </div>
      <MenuSheet dialogRef={sheet} />
    </div>
  )
}

/** The menu below 1024 px: a sheet that closes in the link's own click, so the new screen's heading keeps the focus. */
function MenuSheet({ dialogRef }: { dialogRef: RefObject<HTMLDialogElement | null> }) {
  const { messages: m } = useLocale()
  // Turned to landscape with the menu open, the sidebar is back: the sheet goes, and the
  // focus moves to the sidebar's current item, not back to the menu button now hidden.
  useEffect(() => {
    const wide = window.matchMedia(WIDE)
    const onChange = () => {
      if (!wide.matches || !dialogRef.current?.open) return
      dialogRef.current.close()
      const current = document.querySelector<HTMLElement>('aside a[aria-current="page"]')
      ;(current ?? document.getElementById('main'))?.focus()
    }
    wide.addEventListener('change', onChange)
    return () => wide.removeEventListener('change', onChange)
  }, [dialogRef])
  const close = () => dialogRef.current?.close()
  return (
    <Dialog
      dialogRef={dialogRef}
      label={m.nav.menu}
      className="m-0 h-dvh max-h-dvh max-w-xs rounded-none rounded-e-lg p-4"
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3">
          <Wordmark />
          <Button
            variant="outline"
            className="size-touch shrink-0 rounded-sm px-0"
            aria-label={m.nav.closeMenu}
            onClick={close}
          >
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>
        <FindBooking onNavigate={close} />
        <Menu onNavigate={close} />
        {/* Blocca here too: behind the open sheet the top bar is in sight but cannot be pressed. */}
        <div className="flex flex-col gap-3 border-t border-rule pt-4">
          <Person onNavigate={close} />
          <LockButton />
        </div>
      </div>
    </Dialog>
  )
}

/**
 * The sidebar's menu, the one part of it that scrolls. While more lies below
 * what shows, its foot fades; the padding keeps an item reached by Tab clear
 * of the fade. Positioned, as every region that scrolls is (UI-2f): what is
 * positioned inside it scrolls with it.
 */
function SidebarMenu() {
  const scroll = useRef<HTMLDivElement>(null)
  const { moreBelow } = useScrollState(scroll)
  return (
    <div
      ref={scroll}
      data-more={moreBelow ? '' : undefined}
      className="scroll-fade relative min-h-0 flex-1 scroll-pb-8 overflow-y-auto px-4 pb-4"
    >
      <Menu />
    </div>
  )
}

/** The menu's groups, each item hidden by the same permission as its screen. */
function Menu({ onNavigate }: { onNavigate?: () => void }) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const id = useId()
  return (
    <nav aria-label={m.nav.label} className="flex flex-col gap-4">
      {navGroups(access).map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <p id={`${id}-${group.label}`} className="px-3 text-sm font-medium text-text-muted">
            {m.nav[group.label]}
          </p>
          <ul aria-labelledby={`${id}-${group.label}`} className="flex flex-col">
            {group.items.map((item) => {
              const Icon = ICONS[item.to]
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={menuLink}
                    activeOptions={{ exact: item.to === '/', includeSearch: false }}
                    onClick={onNavigate}
                  >
                    <Icon aria-hidden="true" className="size-4 shrink-0" />
                    {m.nav[item.label]}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}

function NewBooking({ compact = false }: { compact?: boolean }) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  if (!can(access, 'bookings.create')) return null
  return (
    <Link
      to="/bookings/new"
      className={cn(buttonVariants({ variant: 'staff' }), compact && COMPACT_BUTTON)}
    >
      <Plus aria-hidden="true" className="size-4" />
      <span className={compact ? COMPACT_LABEL : undefined}>{m.nav.newBooking}</span>
    </Link>
  )
}

/** A code, a name or a phone number, opened in Prenotazioni. */
function FindBooking({ onNavigate }: { onNavigate?: () => void }) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const navigate = useNavigate()
  const id = useId()
  const [query, setQuery] = useState('')
  if (!can(access, 'bookings.read')) return null

  function submit(event: FormEvent) {
    event.preventDefault()
    const q = query.trim()
    onNavigate?.()
    setQuery('')
    void navigate({ to: '/bookings', search: q ? { q } : {} })
  }

  return (
    // <search> came with Safari 17; on iPadOS 16.4 it would name no landmark.
    // biome-ignore lint/a11y/useSemanticElements: the role reaches every supported browser
    <form
      role="search"
      aria-labelledby={`${id}-label`}
      onSubmit={submit}
      className="flex flex-col gap-1"
    >
      <label id={`${id}-label`} htmlFor={id} className="text-sm font-medium text-text-muted">
        {m.nav.search}
      </label>
      <div className="flex gap-2">
        <StaffInput
          id={id}
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button
          type="submit"
          variant="outline"
          className="size-touch shrink-0 rounded-sm px-0"
          aria-label={m.nav.searchSubmit}
        >
          <Search aria-hidden="true" className="size-4" />
        </Button>
      </div>
    </form>
  )
}

/** Who is unlocked: what is done on this device now is recorded under this name. */
function Who() {
  const { messages: m } = useLocale()
  const session = useSession()
  const role = (m.roles as Readonly<Record<string, string>>)[session.user.role] ?? session.user.role
  return (
    <p className="text-sm font-medium text-text">
      {format(m.nav.signedInAs, { name: session.user.displayName, role })}
    </p>
  )
}

/**
 * UI-2f: no connection, said under who is unlocked in the bar that shows, in
 * the lock screen's words, on a warning chip with its icon. What a screen asks
 * for still says so where it was asked; this says why before anyone presses.
 * A status from the start, empty while connected, so a screen reader hears it
 * change (WCAG 4.1.3); the bar that is hidden at this width is not heard. The
 * connection come back is said too, for a few seconds (UI-2g): the chip going
 * is what a sighted person sees, and its going is silent.
 */
function Offline() {
  const { messages: m } = useLocale()
  const [back, say] = useSaid()
  const online = useOnline(() => say(m.nav.backOnline))
  return (
    <div role="status" data-network="">
      {online ? (
        <span className="sr-only">
          <SaidWords said={back} />
        </span>
      ) : (
        // Compact, with no margin, on a flex line so only the chip's own height counts, not
        // a line of body text (UI-2g): under a one-line name it fits beside the bar's 44 px
        // buttons, so the bar does not grow when the connection goes and move the screen.
        <p className="flex">
          <StatusChip tone="warning" compact>
            {m.nav.offline}
          </StatusChip>
        </p>
      )}
    </div>
  )
}

/** Who is unlocked on which device, and the ways out of it. */
function Person({ onNavigate }: { onNavigate?: () => void }) {
  const { messages: m } = useLocale()
  const session = useSession()
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col">
        <Who />
        <p className="text-sm text-text-muted">
          {format(m.nav.device, { name: session.device.name })}
        </p>
        <Offline />
      </div>
      <div className="flex flex-wrap items-center gap-x-4">
        {session.canSetPin ? (
          <Link to="/pin" className="inline-flex min-h-touch items-center" onClick={onNavigate}>
            {m.nav.changePin}
          </Link>
        ) : null}
        {/* S9: for everyone, however they unlocked: the current password is asked again. */}
        <Link to="/password" className="inline-flex min-h-touch items-center" onClick={onNavigate}>
          {m.nav.changePassword}
        </Link>
        <LanguageSwitch />
      </div>
    </div>
  )
}

function LockButton({ compact = false }: { compact?: boolean }) {
  const { messages: m } = useLocale()
  const { lock } = useAccess()
  return (
    <Button
      variant="outline"
      className={cn('rounded-sm', compact && COMPACT_BUTTON)}
      onClick={() => void lock('locked')}
    >
      <Lock aria-hidden="true" className="size-4" />
      <span className={compact ? COMPACT_LABEL : undefined}>{m.nav.lock}</span>
    </Button>
  )
}

/**
 * Screen to screen (DESIGN.md, "Motion"): the new screen settles in, from
 * the tokens, when the path changes; never on a search, a filter or another
 * page of results, nor when the app opens. Not a view transition: one holds
 * every tap until it ends, and a tap made then is lost. Asked for less
 * motion, the tokens take no time.
 */
function useScreenEntrance(main: RefObject<HTMLElement | null>) {
  const path = useRouterState({ select: (state) => state.location.pathname })
  const shown = useRef(path)
  // Before the first paint of the new screen, so it never shows still first.
  useLayoutEffect(() => {
    const element = main.current
    if (!element || shown.current === path) return
    shown.current = path
    const tokens = getComputedStyle(element)
    animate(
      element,
      [
        { opacity: 0, translate: `0 ${cssToken(tokens, '--space-2')}` },
        { opacity: 1, translate: '0 0' },
      ],
      {
        duration: cssTime(cssToken(tokens, '--duration-state')),
        easing: cssToken(tokens, '--ease-glide') || undefined,
      },
    )
  }, [main, path])
}

/**
 * Below 1024 px the top bar stays at the top of the screen, and it wraps to a
 * second row when it holds more. What is scrolled or focused to the top must
 * land below it (WCAG 2.4.11), so its height is the page's scroll padding
 * (styles.css). The one length the app sets at run time: measured, never a
 * design value. The bar is hidden from 1024 px, and measures 0.
 */
function useTopBarClearance(bar: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const element = bar.current
    if (!element) return
    const root = document.documentElement
    const measure = () => root.style.setProperty('--top-bar', `${element.offsetHeight}px`)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--top-bar')
    }
  }, [bar])
}

/** How often the banner asks: the Sistema screen itself refreshes every minute. */
const BANNER_REFRESH_MS = 5 * 60_000

/**
 * S2: for holders of system.view, a line when something behind the app needs
 * attention, linking to the Sistema screen. Hidden on that screen itself.
 */
function SystemBanner() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'system.view')
  const path = useRouterState({ select: (state) => state.location.pathname })
  const system = useQuery({
    queryKey: SYSTEM_QUERY_KEY,
    queryFn: loadSystem,
    enabled: allowed,
    refetchInterval: BANNER_REFRESH_MS,
    staleTime: SYSTEM_REFRESH_MS,
  })
  if (!allowed || path === '/system' || system.data?.summary !== 'attention') return null
  return (
    <Notice tone="warning">
      <p>
        {m.system.banner}{' '}
        <Link to="/system" className="touch-target">
          {m.system.bannerLink}
        </Link>
      </p>
    </Notice>
  )
}
